/**
 * Puente para usar reCAPTCHA v3 fuera del árbol de React.
 *
 * `react-google-recaptcha-v3` solo expone `executeRecaptcha` vía el hook
 * `useGoogleReCaptcha`. RecaptchaBinder lo registra aquí al montar la app, y
 * la capa de red (NewClassRequest) obtiene un token FRESCO por cada request
 * a un endpoint público, cumpliendo "cada request genera y valida su propio token".
 */
let executor = null;

export const setRecaptchaExecutor = (fn) => {
  executor = typeof fn === 'function' ? fn : null;
};

/**
 * Genera un token nuevo de reCAPTCHA v3 para la `action` indicada. Cada endpoint
 * protegido usa su propia acción (el backend valida que coincida), por eso NO hay
 * acción por defecto: el llamador siempre debe pasar la suya.
 *
 * Si el executor aún no fue registrado (el script de Google tarda en cargar en el
 * primer request de la página), espera brevemente a que RecaptchaBinder lo registre
 * antes de rendirse, para no enviar la primera consulta legítima sin token (falso
 * positivo 403). Devuelve null si tras la espera sigue sin estar listo o si Google
 * falla, para que la capa de red decida cómo continuar (fail-closed).
 */
export const getRecaptchaToken = async (action, { timeoutMs = 3000, intervalMs = 100 } = {}) => {
  const deadline = Date.now() + timeoutMs;
  while (typeof executor !== 'function' && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  if (typeof executor !== 'function') {
    return null;
  }
  try {
    return await executor(action);
  } catch {
    return null;
  }
};
